/**
 * The Big 5 valuation metrics vs the plan anchors ("Go for Gold"
 * Dec 2026, "Exit" Dec 2027), actuals fed live from the LTM story.
 * Sits at the top of the Outlook tab.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { PanelTitle } from "../staff/CommissionedPanels";
import { Card, InfoTip } from "./cfoShared";

const API = process.env.REACT_APP_BACKEND_URL;

const fmtVal = (v, unit) => {
  if (v == null) return "–";
  if (unit === "pct") return `${v.toFixed(1)}%`;
  return `£${(v / 1_000_000).toFixed(2)}m`;
};

export function GoalsPanel() {
  const [goals, setGoals] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/quickbooks/story`)
      .then((r) => setGoals(r.data?.goals || null))
      .catch(() => setGoals(null));
  }, []);

  if (!goals) return null;

  return (
    <Card testid="outlook-goals-card">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <PanelTitle>The Big 5 · valuation metrics <InfoTip id="out_big5" /></PanelTitle>
        <span className="text-[10px] font-mono text-stone-400">LTM to {goals.as_of}</span>
      </div>
      <table className="w-full text-xs mt-3">
        <thead>
          <tr className="text-[10px] font-mono uppercase tracking-wider text-stone-400 border-b border-stone-100">
            <th className="text-left pb-1.5">Metric</th>
            <th className="text-right pb-1.5">Actual</th>
            <th className="text-left pb-1.5 pl-4 w-1/3">Progress to {goals.labels?.fy26 || "FY26"}</th>
            <th className="text-right pb-1.5">Target</th>
            <th className="text-right pb-1.5">{goals.labels?.fy27 || "FY27"}</th>
          </tr>
        </thead>
        <tbody>
          {(goals.metrics || []).map((m) => {
            const pct = m.actual != null && m.fy26 ? Math.min((m.actual / m.fy26) * 100, 120) : 0;
            const hit = m.actual != null && m.actual >= m.fy26;
            return (
              <tr key={m.id} className="border-b border-stone-50" data-testid={`goals-row-${m.id}`}>
                <td className="py-2 text-slate-800">{m.label}</td>
                <td className={`py-2 text-right font-mono tabular-nums font-semibold ${hit ? "text-teal-700" : "text-slate-900"}`}>
                  {fmtVal(m.actual, m.unit)}
                </td>
                <td className="py-2 pl-4">
                  <div className="h-2 rounded-full bg-stone-100 overflow-hidden">
                    <div
                      className={`h-full rounded-full ${hit ? "bg-teal-600" : "bg-[#1B4F8A]/70"}`}
                      style={{ width: `${Math.max(Math.min(pct, 100), 2)}%` }}
                    />
                  </div>
                </td>
                <td className="py-2 text-right font-mono tabular-nums text-stone-500">{fmtVal(m.fy26, m.unit)}</td>
                <td className="py-2 text-right font-mono tabular-nums text-stone-400">{fmtVal(m.fy27, m.unit)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Card>
  );
}
